import React, { useRef, useState } from 'react';
import { Box, ButtonBase, Paper, Popper, Typography } from '@mui/material';
import { API_BASE_URL } from '../../../config';
import { launchColors } from '../launchTheme';

export function assetThumbnailUrl(assetId: string): string {
  const token = localStorage.getItem('authToken');
  const url = `${API_BASE_URL}/api/assets/${encodeURIComponent(assetId)}/preview/thumbnail`;
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
}

export function openAssetInAthena(assetId: string): void {
  window.dispatchEvent(new CustomEvent('dam:open-asset-details', {
    detail: { assetId, preserveCurrentView: true },
  }));
}

interface Props {
  assetId: string;
  title: string;
  hasThumbnail: boolean;
  children: React.ReactNode;
}

function useHoverAnchor() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = (element: HTMLElement) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setAnchor(element), 150);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    setAnchor(null);
  };
  return { anchor, show, hide };
}

const PreviewPopper: React.FC<{ anchor: HTMLElement | null; assetId: string; title: string; hasThumbnail: boolean; hint: string }> = ({ anchor, assetId, title, hasThumbnail, hint }) => {
  const [imageFailed, setImageFailed] = useState(false);
  return (
    <Popper open={Boolean(anchor)} anchorEl={anchor} placement="right-start" sx={{ zIndex: 1500, pointerEvents: 'none' }}>
      <Paper elevation={6} sx={{ p: 1, width: 196, borderRadius: '10px', border: `1px solid ${launchColors.divider}` }}>
        {hasThumbnail && !imageFailed ? (
          <Box
            component="img"
            src={assetThumbnailUrl(assetId)}
            alt={title}
            onError={() => setImageFailed(true)}
            sx={{ width: '100%', height: 160, objectFit: 'contain', bgcolor: launchColors.section, borderRadius: '6px', display: 'block' }}
          />
        ) : (
          <Box sx={{ height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: launchColors.section, borderRadius: '6px' }}>
            <Typography sx={{ fontSize: 12, color: launchColors.textMuted }}>Ingen bild</Typography>
          </Box>
        )}
        <Typography sx={{ mt: 0.75, fontSize: 13, fontWeight: 600, color: launchColors.text }}>{title}</Typography>
        <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>{hint}</Typography>
      </Paper>
    </Popper>
  );
};

const AssetHoverLink: React.FC<Props> = ({ assetId, title, hasThumbnail, children }) => {
  const { anchor, show, hide } = useHoverAnchor();

  return (
    <>
      <ButtonBase
        disableRipple
        aria-label={`Öppna ${title} i Athena`}
        onMouseEnter={(event) => show(event.currentTarget)}
        onMouseLeave={hide}
        onFocus={(event) => show(event.currentTarget)}
        onBlur={hide}
        onClick={(event) => {
          event.stopPropagation();
          hide();
          openAssetInAthena(assetId);
        }}
        sx={{
          font: 'inherit',
          fontWeight: 600,
          color: launchColors.navy,
          textDecoration: 'underline',
          textDecorationColor: launchColors.petrol,
          textUnderlineOffset: '3px',
          borderRadius: '4px',
          '&:hover': { color: launchColors.petrolDark },
          '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: 2 },
        }}
      >
        {children}
      </ButtonBase>
      <PreviewPopper anchor={anchor} assetId={assetId} title={title} hasThumbnail={hasThumbnail} hint="Klicka för att öppna i Athena" />
    </>
  );
};

export const AssetHoverPreview: React.FC<Props> = ({ assetId, title, hasThumbnail, children }) => {
  const { anchor, show, hide } = useHoverAnchor();
  return (
    <>
      <Box
        component="span"
        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => show(event.currentTarget)}
        onMouseLeave={hide}
        sx={{ textDecoration: 'underline', textDecorationColor: launchColors.petrol, textUnderlineOffset: '3px' }}
      >
        {children}
      </Box>
      <PreviewPopper anchor={anchor} assetId={assetId} title={title} hasThumbnail={hasThumbnail} hint="Finns i Athena" />
    </>
  );
};

export default AssetHoverLink;
